import { useCallback, useEffect } from 'react';
import { useNotificationStore } from '../store/notificationStore';
import { useChatStore } from '../store/chatStore';
import { useAuthStore } from '../store/authStore';
import { useSSE } from './useSSE';
import { fetchUnreadCount } from '../api/notifications';
import { listMyRooms } from '../api/chat';
import { isHiddenNotification, isMinutesNotification } from '../lib/notification-source';
import { useMinutesNotifyStore } from '../store/minutesNotifyStore';

/**
 * 레이아웃 레벨 알림/미읽음 단일 호스트 훅 (design-20260717 Phase 2-A).
 * docs/design/design-20260717-common-layout-skeleton.md
 *
 * 원래 `TopBar` 안에 있던 `/sse/me` 구독 + 알림/채팅 미읽음 동기화 로직을 **렌더에서 분리**해
 * `AppLayout` 레벨의 headless 호스트(`LayoutNotificationHost`)로 이관한 것. 이렇게 하면 상단 TopBar
 * 를 하단 StatusBar 로 교체/제거해도 알림 hook 소유자가 항상 **정확히 1개**로 유지되어 이중 구독/
 * 미읽음 유실(설계 R1)을 구조적으로 차단한다.
 *
 * ⚠ 앱 전역에서 **한 번만** mount 되어야 한다(중복 mount = /sse/me 이중 구독). AppLayout 이 유일 호출자.
 * 로직·동작은 이관 전 TopBar 와 동일(무회귀).
 */
export function useLayoutNotifications(): void {
  const setUnread = useNotificationStore((s) => s.setUnread);
  const incrementUnread = useNotificationStore((s) => s.incrementUnread);
  const setRoomUnreads = useChatStore((s) => s.setRoomUnreads);
  const bumpRoomUnread = useChatStore((s) => s.bumpRoomUnread);
  const userId = useAuthStore((s) => s.user?.id?.toString() ?? '');
  const bumpMinutesNotify = useMinutesNotifyStore((s) => s.bumpMinutesNotify);

  // 채팅 미읽음 전역 동기화 — listMyRooms 결과를 roomId→미읽음 맵으로 chatStore 에 반영.
  // AppLayout level 에서 항상 mount → 어느 화면에서도 NavBar 배지 + 방목록 배지 표시.
  const refreshChatUnread = useCallback(() => {
    if (!userId) return;
    listMyRooms({ limit: 200 })
      .then((res) => {
        const map: Record<string, number> = {};
        for (const r of res.rooms) map[r.id] = r.unreadCount ?? 0;
        setRoomUnreads(map);
        // 보고 있는 방은 읽음 유지 (재조회로 backend lastReadId 지연분이 배지로 재등장하지 않게).
        const active = useChatStore.getState().activeChatId;
        if (active) useChatStore.getState().clearRoomUnread(active);
      })
      .catch(() => {
        // 실패 시 기존 값 유지 — 다음 SSE bump / 재마운트 시 회복.
      });
  }, [userId, setRoomUnreads]);

  // 초기 mount 시 unread-count 동기화 — SSE 연결 전 DB 상태 표시.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    fetchUnreadCount()
      .then((count) => {
        if (!cancelled) setUnread(count);
      })
      .catch(() => {
        // 실패 시 0 유지 (현재 store 값) — SSE 가 추후 increment 시 회복.
      });
    return () => { cancelled = true; };
  }, [userId, setUnread]);

  // 초기 mount 시 채팅 미읽음 합계 동기화 (어느 화면에서 진입해도 NavBar 배지 표시).
  useEffect(() => {
    refreshChatUnread();
  }, [refreshChatUnread]);

  // /sse/me single source — layout level 에서 모든 user-channel 이벤트 fan-out:
  //   - onNotification → notificationStore.incrementUnread (badge 갱신 + dropdown bump)
  //   - 채팅 메시지가 알림을 동반하면 같은 이벤트로 채팅 미읽음도 재동기화.
  //
  // 2026-08-12 (사용자 요구) — **채팅 발화 알림은 벨 카운터를 올리지 않는다.**
  //   "채팅으로 들어온 내역은 알림에 넣지 말고 채팅 상태바에만." 서버도 알림함 조회에서 같은
  //   source 를 빼므로(buildHiddenSourceWhere), 여기서 올리면 새로고침 전까지 "숫자는 있는데
  //   목록은 빈" 상태가 된다. 채팅 미읽음 재동기화(refreshChatUnread)는 그대로 — 그게 상태바
  //   채팅 배지의 갱신 경로다.
  useSSE({
    mode: 'me',
    enabled: Boolean(userId),
    onNotification: (notif) => {
      if (!isHiddenNotification(notif)) incrementUnread();
      refreshChatUnread();
      /*
       * ⭐ 2026-10-09 (A-1b) — 회의록 알림이면 ★ 「왔다」를 ⭐ 알린다.
       *
       * ⛔ 여기서 ★ «무엇이 바뀌었나»를 보지 않는다 — 알림에 ⭐ 회의록 id 가 «없다»(`sourceId` 는 null).
       * ⇒ ★ 그래서 ⭐ tick 하나만 올리고 ⇒ ★ 「무엇이」는 ⭐ 회의록 화면이 «제 것을 다시 읽어» 본다.
       *   까닭은 `store/minutesNotifyStore.ts` 머리말에 있다.
       * ⚠ 그리고 ★ `isHiddenNotification` 과 ⭐ «다른 질문»이라 `else` 로 묶지 않았다 —
       *   그것은 「벨에 셀까」이고 이것은 ★ 「목록을 다시 읽을까」다. ⇒ 회의록 알림은 ⭐ 벨에도 «든다».
       */
      if (isMinutesNotification(notif)) bumpMinutesNotify();
    },
    // 실시간 채팅 미읽음 — 보고 있지 않은 방에 새 메시지 도착 시 즉시 +1 (활성 방은 ChatPage 가 읽음 처리).
    onChatUnread: ({ roomId }) => {
      if (useChatStore.getState().activeChatId === roomId) return;
      bumpRoomUnread(roomId);
    },
  });
}
